import { useCallback, useEffect, useMemo, useState } from 'react'

import { Autocomplete, cn, Description, EmptyState, Label, ListBox, SearchField, Spinner } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { driversApi } from '@/api/drivers.api'
import { useDrivers } from '@/hooks/useDrivers'
import type { Driver } from '@/types/driver'

import { Item } from './Item'

interface DriverAutocompleteProps {
  label?: string;
  description?: string;
  placeholder?: string;
  onSelectionChange?: (driver: Driver | null) => void;
  className?: string;
  selectedId?: string;
  initialValue?: Driver | null;
  isInvalid?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
  isDisabled?: boolean;
}

export default ({
  label,
  description,
  placeholder,
  onSelectionChange,
  className,
  selectedId,
  initialValue,
  isInvalid,
  isRequired,
  isDisabled,
}: DriverAutocompleteProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)

  const hasSelectedValue = Boolean(selectedId && selectedId !== 'none')
  const enabled = isOpen ? true : isFocused ? true : hasSelectedValue
  const { data, isLoading } = useDrivers(search, enabled)

  useEffect(() => {
    if (!selectedId || selectedId === 'none') {
      setSelectedDriver(null)
      return
    }

    if (selectedDriver?.ID === selectedId) {
      return
    }

    const found = data.find(d => d.ID === selectedId) || (initialValue?.ID === selectedId ? initialValue : null)
    if (found) {
      setSelectedDriver(found)
      return
    }

    let isMounted = true
    driversApi.getDriver(selectedId)
      .then(driver => {
        if (isMounted && driver) {
          setSelectedDriver(driver)
        }
      })
      .catch(err => {
        console.error('Error fetching driver details:', err)
      })

    return () => {
      isMounted = false
    }
  }, [selectedId, data, initialValue, selectedDriver])

  const items = useMemo(() => {
    const list = [...data]
    if (initialValue && !list.some(d => d.ID === initialValue.ID)) {
      list.unshift(initialValue)
    }
    if (selectedDriver && selectedDriver.ID === selectedId && !list.some(d => d.ID === selectedDriver.ID)) {
      list.unshift(selectedDriver)
    }
    return list
  }, [data, initialValue, selectedDriver, selectedId])

  const finalPlaceholder = placeholder || t('driver_autocomplete.placeholder')

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  const handleSelectionChange = useCallback((key: React.Key | null) => {
    const driver = items.find(d => d.ID === key) ?? null
    onSelectionChange?.(driver)
  }, [items, onSelectionChange])

  return (
    <Autocomplete
      items={items as any}
      allowsEmptyCollection
      variant={'secondary'}
      className={className}
      selectedKey={selectedId || null}
      isInvalid={isInvalid}
      isRequired={isRequired}
      isDisabled={isDisabled}
      onSelectionChange={handleSelectionChange}
      onOpenChange={handleOpenChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {label && <Label>{label}</Label>}
      <Autocomplete.Trigger className={cn("w-full min-w-0 overflow-hidden", isDisabled ? "pointer-events-none opacity-80" : "")}>
        <Autocomplete.Value className="w-full min-w-0 overflow-hidden">
          {({ isPlaceholder, state }) => {
            if (isPlaceholder) return finalPlaceholder

            const selectedKey = state.selectedKey
            const item = items.find(d => d.ID === selectedKey)

            if (!item) return null

            return (
              <div className="flex items-center gap-2 w-full min-w-0 overflow-hidden whitespace-nowrap">
                <span className="font-medium text-sm truncate min-w-0">{item.Name}</span>
              </div>
            )
          }}
        </Autocomplete.Value>
        {selectedId && selectedId !== 'none' && !isDisabled && (
          <div onClick={e => e.stopPropagation()}>
            <Autocomplete.ClearButton />
          </div>
        )}
        <Autocomplete.Indicator />
      </Autocomplete.Trigger>
      {description && <Description>{description}</Description>}
      <Autocomplete.Popover>
        <Autocomplete.Filter>
          <SearchField
            onChange={val => {
              setSearch(val)
            }}
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={t('driver_autocomplete.search_placeholder')} autoFocus />
              <Spinner
                size="sm"
                className={cn("absolute top-1/2 right-2 -translate-y-1/2", {
                  "pointer-events-none opacity-0": !isLoading,
                })}
              />
            </SearchField.Group>
          </SearchField>
          <ListBox
            items={items as any}
            renderEmptyState={() => <EmptyState>{t('driver_autocomplete.no_results')}</EmptyState>}
          >
            {(item: Driver) => (
              <ListBox.Item key={item.ID} id={item.ID} textValue={item.Name} >
                <Item item={item} />
                <ListBox.ItemIndicator />
              </ListBox.Item>
            )}
          </ListBox>
        </Autocomplete.Filter>
      </Autocomplete.Popover>
    </Autocomplete>
  )
}
